import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { DemoModal } from '../components/DemoModal';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
export function ContactPage() {
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitted(true);
    setTimeout(() => setIsSubmitted(false), 5000);
  };
  return <motion.main initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} transition={{
    duration: 0.5
  }} className="min-h-screen bg-stark-white" data-id="element-262">
      <Header onOpenDemo={() => setIsDemoModalOpen(true)} data-id="element-263" />

      <section className="pt-32 pb-20 bg-midnight-navy text-stark-white relative overflow-hidden" data-id="element-264">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-accent-red/5 skew-x-12 translate-x-32 pointer-events-none" data-id="element-265" />
        <div className="max-w-7xl mx-auto px-6 relative z-10 text-center" data-id="element-266">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="max-w-3xl mx-auto" data-id="element-267">
            <h1 className="text-5xl md:text-6xl font-manrope font-bold mb-6" data-id="element-268">
              Get in Touch
            </h1>
            <p className="text-xl text-stark-white/70 leading-relaxed" data-id="element-269">
              Have questions about how ROI can transform your restaurant? Our
              team of operational specialists is here to help.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-24" data-id="element-270">
        <div className="max-w-7xl mx-auto px-6" data-id="element-271">
          <div className="grid lg:grid-cols-3 gap-12" data-id="element-272">
            {/* Contact Info */}
            <div className="lg:col-span-1 space-y-12" data-id="element-273">
              <div className="space-y-8" data-id="element-274">
                <h2 className="text-3xl font-manrope font-bold text-midnight-navy" data-id="element-275">
                  Contact Information
                </h2>
                <p className="text-midnight-navy/60 text-lg leading-relaxed" data-id="element-276">
                  Reach out to us directly or fill out the form and we'll get
                  back to you within 24 hours.
                </p>
              </div>

              <div className="space-y-6" data-id="element-277">
                {[{
                icon: Mail,
                label: 'Email Us',
                value: 'hello@roi-intelligence.com'
              }, {
                icon: Phone,
                label: 'Call Us',
                value: '+1 (555) 123-4567'
              }, {
                icon: MapPin,
                label: 'Visit Us',
                value: '123 Tech Plaza, San Francisco, CA'
              }].map((item, idx) => <motion.div key={idx} initial={{
                opacity: 0,
                x: -20
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: idx * 0.1
              }} className="flex items-start gap-4" data-id="element-278">
                    <div className="w-12 h-12 bg-midnight-navy text-stark-white rounded-xl flex items-center justify-center shrink-0" data-id="element-279">
                      <item.icon className="w-6 h-6" data-id="element-280" />
                    </div>
                    <div data-id="element-281">
                      <div className="text-xs font-bold uppercase tracking-widest text-midnight-navy/40 mb-1" data-id="element-282">
                        {item.label}
                      </div>
                      <div className="text-lg font-semibold text-midnight-navy" data-id="element-283">
                        {item.value}
                      </div>
                    </div>
                  </motion.div>)}
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2" data-id="element-284">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} className="bg-cool-gray p-8 md:p-12 rounded-3xl border border-gray-200 shadow-xl" data-id="element-285">
                {!isSubmitted ? <form onSubmit={handleSubmit} className="space-y-6" data-id="element-286">
                    <div className="grid md:grid-cols-2 gap-6" data-id="element-287">
                      <div className="space-y-2" data-id="element-288">
                        <label className="text-xs font-bold uppercase tracking-wider text-midnight-navy/40 ml-1" data-id="element-289">
                          Your Name
                        </label>
                        <input required type="text" className="w-full px-6 py-4 bg-stark-white border border-gray-200 rounded-2xl focus:outline-none focus:border-accent-red transition-colors" placeholder="Jane Doe" data-id="element-290" />
                      </div>
                      <div className="space-y-2" data-id="element-291">
                        <label className="text-xs font-bold uppercase tracking-wider text-midnight-navy/40 ml-1" data-id="element-292">
                          Email Address
                        </label>
                        <input required type="email" className="w-full px-6 py-4 bg-stark-white border border-gray-200 rounded-2xl focus:outline-none focus:border-accent-red transition-colors" placeholder="jane@example.com" data-id="element-293" />
                      </div>
                    </div>
                    <div className="space-y-2" data-id="element-294">
                      <label className="text-xs font-bold uppercase tracking-wider text-midnight-navy/40 ml-1" data-id="element-295">
                        Subject
                      </label>
                      <input required type="text" className="w-full px-6 py-4 bg-stark-white border border-gray-200 rounded-2xl focus:outline-none focus:border-accent-red transition-colors" placeholder="How can we help?" data-id="element-296" />
                    </div>
                    <div className="space-y-2" data-id="element-297">
                      <label className="text-xs font-bold uppercase tracking-wider text-midnight-navy/40 ml-1" data-id="element-298">
                        Message
                      </label>
                      <textarea required rows={6} className="w-full px-6 py-4 bg-stark-white border border-gray-200 rounded-2xl focus:outline-none focus:border-accent-red transition-colors resize-none" placeholder="Tell us more about your restaurant..." data-id="element-299" />
                    </div>
                    <button type="submit" className="w-full md:w-auto bg-accent-red text-stark-white px-12 py-5 rounded-full font-bold hover:bg-red-700 transition-all shadow-xl shadow-accent-red/20 flex items-center justify-center gap-3 group" data-id="element-300">
                      Send Message
                      <Send className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" data-id="element-301" />
                    </button>
                  </form> : <motion.div initial={{
                opacity: 0,
                scale: 0.9
              }} animate={{
                opacity: 1,
                scale: 1
              }} className="py-20 text-center space-y-6" data-id="element-302">
                    <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto" data-id="element-303">
                      <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-304">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" data-id="element-305" />
                      </svg>
                    </div>
                    <div className="space-y-2" data-id="element-306">
                      <h3 className="text-3xl font-manrope font-bold text-midnight-navy" data-id="element-307">
                        Message Sent!
                      </h3>
                      <p className="text-midnight-navy/60 text-lg" data-id="element-308">
                        Thank you for reaching out. We'll be in touch very soon.
                      </p>
                    </div>
                  </motion.div>}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <Footer data-id="element-309" />
      <DemoModal isOpen={isDemoModalOpen} onClose={() => setIsDemoModalOpen(false)} data-id="element-310" />
    </motion.main>;
}